import React, { useState } from 'react'
import { ChallengeService } from '@duck/api-definitions'
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  InlineConfirm,
  cn,
} from '@duck/ui'
import { toast } from 'sonner'
import { Link } from '@tanstack/react-router'
import { useTar } from '@/modules'
import { apis } from '@/modules/apiClients'
import { StandardLoader } from '@/components/StandardLoader'
import {
  ArrowPathIcon,
  ChartBarIcon,
  ArrowUpOnSquareIcon,
  ArrowUturnLeftIcon,
  ArrowDownTrayIcon,
  ChevronDownIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'

interface ChallengeActionsProps {
  challengeInfo: ChallengeService.ChallengeInfo
  onChanged: () => void
  onDeleted: () => void
}

type ConfirmableAction = 'resubmit' | 'unsubmit' | 'delete'

const errorMessage = (error: unknown): string =>
  (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
  (error as Error)?.message ??
  'Unknown error'

export const ChallengeActions: React.FC<ChallengeActionsProps> = ({
  challengeInfo,
  onChanged,
  onDeleted,
}) => {
  const { t } = useTar()
  const { challenge } = challengeInfo
  const challengeId = challenge.id

  const [menuOpen, setMenuOpen] = useState(false)
  // Which destructive-ish item is currently showing its inline confirm row
  const [armed, setArmed] = useState<ConfirmableAction | null>(null)
  const [pending, setPending] = useState<ConfirmableAction | null>(null)

  // Optimistic release state (mirrors ChallengesTable pattern)
  const [releaseOverride, setReleaseOverride] = useState<boolean | undefined>(undefined)
  const [releasePending, setReleasePending] = useState(false)

  const [pdfDownloading, setPdfDownloading] = useState(false)

  const effectiveRelease = releaseOverride ?? Boolean(challenge.feedbackReleaseOverride)
  const isFinished = challenge.status === ChallengeService.ChallengeStatusEnum.Finished
  const isProcessed = challenge.feedbackStatus === ChallengeService.FeedbackStatusEnum.Processed

  const handleMenuOpenChange = (open: boolean) => {
    setMenuOpen(open)
    if (!open) setArmed(null)
  }

  const handleToggleFeedbackRelease = async () => {
    if (releasePending) return
    const newValue = !effectiveRelease
    setReleasePending(true)
    setReleaseOverride(newValue)
    try {
      await apis.challengeService.releaseFeedback({ challengeId, release: newValue })
      onChanged()
      setReleaseOverride(undefined)
    } catch (error) {
      console.error('Failed to update feedback release override', { challengeId, release: newValue, error })
      setReleaseOverride(!newValue)
      toast.error(t('common.anErrorOccurred'))
    } finally {
      setReleasePending(false)
    }
  }

  const handleDownloadFeedbackPdf = async () => {
    if (pdfDownloading) return
    setPdfDownloading(true)
    try {
      const response = await apis.challengeService.getFeedbackPdf(challengeId, {
        responseType: 'blob',
      })
      const blob = response.data as Blob
      const contentDisposition = response.headers['content-disposition'] as string | undefined
      let fileName: string | undefined
      if (contentDisposition) {
        const filenameStarMatch = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i)
        if (filenameStarMatch?.[1]) {
          try {
            fileName = decodeURIComponent(filenameStarMatch[1])
          } catch {
            // fall back to basic filename
          }
        }
        if (!fileName) {
          const fileNameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/)
          fileName = fileNameMatch?.[1]
        }
      }
      fileName = fileName ?? `feedback_${challengeId}.pdf`
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch {
      toast.error(t('challenges.failedToDownloadPdf'))
    } finally {
      setPdfDownloading(false)
    }
  }

  const runConfirmed = async (action: ConfirmableAction) => {
    if (pending) return
    setPending(action)
    try {
      if (action === 'resubmit') {
        await apis.challengeService.resubmitChallenge({ challengeId })
        toast.success(t('challenges.resubmitSuccess'))
        onChanged()
      } else if (action === 'unsubmit') {
        await apis.challengeService.unsubmitChallenge({ challengeId })
        toast.success(t('challenges.unsubmitSuccess'))
        onChanged()
      } else {
        await apis.challengeService.deleteChallenge(challengeId)
        onDeleted()
      }
      setArmed(null)
      setMenuOpen(false)
    } catch (error: unknown) {
      console.error(`Failed to ${action} challenge`, { challengeId, error })
      const failedKey = {
        resubmit: 'challenges.resubmitFailed',
        unsubmit: 'challenges.unsubmitFailed',
        delete: 'common.anErrorOccurred',
      } as const
      toast.error(`${t(failedKey[action])}: ${errorMessage(error)}`)
      setArmed(null)
    } finally {
      setPending(null)
    }
  }

  // An item that flips into an InlineConfirm row on first click, keeping the menu open
  const confirmableItem = (
    action: ConfirmableAction,
    icon: React.ReactNode,
    label: string,
    hint: string,
    destructive = false
  ) =>
    armed === action ? (
      <div className="px-1 py-1">
        <InlineConfirm
          variant={destructive ? 'danger' : 'warning'}
          message={t('common.areYouSure')}
          onConfirm={() => { void runConfirmed(action) }}
          onCancel={() => setArmed(null)}
          confirmDisabled={pending === action}
        />
      </div>
    ) : (
      <DropdownMenuItem
        onSelect={(event) => { event.preventDefault(); setArmed(action) }}
        disabled={pending !== null}
        className={cn('group', destructive && 'text-destructive focus:text-destructive')}
      >
        {actionRow(icon, label, hint, destructive)}
      </DropdownMenuItem>
    )

  const actionRow = (icon: React.ReactNode, label: string, hint: string, destructive = false) => (
    <div className="flex items-start gap-2.5 py-0.5">
      <span className={cn('mt-0.5 shrink-0', destructive ? 'text-destructive' : 'text-muted-foreground group-focus:text-accent-foreground')}>{icon}</span>
      <span className="flex flex-col gap-0.5">
        <span className="font-medium leading-tight">{label}</span>
        <span className={cn('text-xs leading-snug', destructive ? 'text-destructive/70 group-focus:text-destructive' : 'text-muted-foreground group-focus:text-accent-foreground')}>{hint}</span>
      </span>
    </div>
  )

  return (
    <div className="flex flex-wrap gap-2 justify-end">
      <Button variant="outline" size="sm" asChild>
        <Link to="/admin/challenges/$slug/activity" params={{ slug: challenge.slug }}>
          <ChartBarIcon className="h-4 w-4 me-1.5" />
          {t('adminActivityReport.openButton')}
        </Link>
      </Button>

      {/* Download PDF — enabled once feedback is processed */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => { void handleDownloadFeedbackPdf() }}
        disabled={!isProcessed || pdfDownloading}
      >
        {pdfDownloading ? (
          <>
            <StandardLoader size="sm" inline className="!m-0 me-1.5" />
            {t('common.generating')}
          </>
        ) : (
          <>
            <ArrowDownTrayIcon className="h-4 w-4 me-1.5" />
            {t('challenges.downloadFeedbackPdf')}
          </>
        )}
      </Button>

      <DropdownMenu open={menuOpen} onOpenChange={handleMenuOpenChange}>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" data-testid="challenge-actions-trigger">
            {t('common.actions')}
            <ChevronDownIcon className="h-4 w-4 ms-1.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-80 p-1.5">
          {/* Release / Unrelease — immediate */}
          <DropdownMenuItem
            onSelect={() => { void handleToggleFeedbackRelease() }}
            disabled={releasePending}
            className="group"
          >
            {actionRow(
              <ArrowUpOnSquareIcon className="h-4 w-4" />,
              effectiveRelease ? t('challenges.unreleaseFeedback') : t('challenges.releaseFeedback'),
              effectiveRelease ? t('challenges.unreleaseFeedbackHint') : t('challenges.releaseFeedbackHint')
            )}
          </DropdownMenuItem>

          {isFinished && (
            <>
              {confirmableItem('resubmit', <ArrowPathIcon className="h-4 w-4" />, t('challenges.resubmit'), t('challenges.resubmitHint'))}
              {confirmableItem('unsubmit', <ArrowUturnLeftIcon className="h-4 w-4" />, t('challenges.unsubmit'), t('challenges.unsubmitHint'))}
            </>
          )}

          <DropdownMenuSeparator />

          {confirmableItem('delete', <TrashIcon className="h-4 w-4" />, t('common.delete'), t('challenges.deleteChallengeHint'), true)}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
